import { Controller } from "@hotwired/stimulus"
import { useTransition } from "stimulus-use"
import { createPopper } from "@popperjs/core"

export default class extends Controller {
  static targets = ["content", "trigger", "arrow"]
  static values = {
    dismissAfter: Number,
    placement: { type: String, default: "top" },
    open: { type: Boolean, default: false }
  }

  connect() {
    this.popper = null
    this.isTouch = window.matchMedia("(hover: none)").matches

    if (this.isTouch) {
      this.triggerTarget.addEventListener("click", this.toggle.bind(this))
    } else {
      this.triggerTarget.addEventListener("mouseenter", this.show.bind(this))
      this.triggerTarget.addEventListener("mouseleave", this.hide.bind(this))
    }

    useTransition(this, {
      element: this.contentTarget,
      enterActive: this.contentTarget.dataset.transitionEnterActive,
      enterFrom: this.contentTarget.dataset.transitionEnterFrom,
      enterTo: this.contentTarget.dataset.transitionEnterTo,
      leaveActive: this.contentTarget.dataset.transitionLeaveActive,
      leaveFrom: this.contentTarget.dataset.transitionLeaveFrom,
      leaveTo: this.contentTarget.dataset.transitionLeaveTo,
      transitioned: this.openValue
    })
  }

  openValueChanged() {
    if (this.openValue) {
      this.initPopper()

      if (this.shouldAutoDismiss) {
        this.scheduleDismissal()
      }
    } else {
      this.destroyPopper()
    }
  }

  initPopper() {
    if (this.popper) this.popper.destroy()

    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const contentRect = this.contentTarget.getBoundingClientRect()

    const spaceAbove = triggerRect.top
    const needs = contentRect.height + 8
    const preferTop = spaceAbove >= (needs * 1.5)
    const placement = preferTop ? this.placementValue : "bottom"

    this.popper = createPopper(this.triggerTarget, this.contentTarget, {
      placement,
      modifiers: [
        {
          name: "offset",
          options: { offset: [0, 6] }
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["bottom", "left", "right"],
            rootBoundary: "clippingParents"
          }
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
          }
        },
        {
          name: "arrow",
          options: {
            element: this.arrowTarget,
          }
        }
      ]
    })
    this.popper.update()
  }

  destroyPopper() {
    if (this.popper) {
      this.popper.destroy()
      this.popper = null
    }
  }

  show() {
    if (this.shouldAutoDismiss) this.scheduleDismissal()
    this.openValue = true
    this.contentTarget.classList.remove("hidden")
  }

  hide() {
    this.openValue = false
    this.contentTarget.classList.add("hidden")
  }

  toggle() {
    this.openValue = !this.openValue
    this.contentTarget.classList.toggle("hidden", !this.openValue)
  }

  get shouldAutoDismiss() {
    return (this.openValue && this.hasDismissAfterValue)
  }

  scheduleDismissal() {
    if (!this.hasDismissAfterValue && this.isTouch) return

    this.cancelDismissal()

    this.timeoutId = setTimeout(() => {
      this.hide()
      this.timeoutId = undefined
    }, this.dismissAfterValue)
  }

  cancelDismissal() {
    if (typeof this.timeoutId === "number") {
      clearTimeout(this.timeoutId)
      this.timeoutId = undefined
    }
  }

  disconnect() {
    if (this.isTouch) {
      this.element.removeEventListener("click", this.toggle)
    } else {
      this.element.removeEventListener("mouseenter", this.show)
      this.element.removeEventListener("mouseleave", this.hide)
    }

    if (this.popper) this.popper.destroy()
  }
}
